<template>
    <div
        v-if="count > 0"
        class="mb-2 border-b border-gray-200 pb-2"
    >
        <h3 class="mb-1 font-bold">{{ $t(title) }} ({{ count }}):</h3>
        <ul>
            <li
                v-for="(entry, index) in entries.slice(0, limit)"
                :key="`${title}-${index}`"
                class="mb-1"
            >
                <router-link
                    v-if="path !== '/accounts' && path !== '/emails'"
                    :to="`${path}/${entry[entryPropertyInPath]}?global-search=${search}`"
                    class="text-blue-500 hover:text-blue-600"
                    @click="handleRecentSearch(entry)"
                >
                    <fa
                        icon="link"
                        class="text-sm"
                    ></fa>
                    {{ entry[entryPropertyToShow] }}
                </router-link>
                <router-link
                    v-if="path === '/emails'"
                    :to="`${path}/${entry[entryPropertyInPath]}?${listFilterName}=${search}&global-search=${search}`"
                    class="text-blue-500 hover:text-blue-600"
                    @click="handleRecentSearch(entry)"
                >
                    <fa
                        icon="link"
                        class="text-sm"
                    ></fa>
                    {{ entry[entryPropertyToShow] }}
                </router-link>
                <router-link
                    v-if="path === '/accounts'"
                    :to="`${
                        entry['account_type'] === 'natural'
                            ? '/naturalProfile/' + entry['natural_profile_uid']
                            : '/legalProfile/' + entry['legal_profile_uid']
                    }?global-search=${search}`"
                    class="text-blue-500 hover:text-blue-600"
                    @click="handleRecentSearch(entry)"
                >
                    <fa
                        icon="link"
                        class="text-sm"
                    ></fa>
                    {{ entry[entryPropertyToShow] }}
                </router-link>
            </li>
            <li v-if="count - limit > 0">
                <router-link
                    :to="`${path}?${listFilterName}=${search}&global-search=${search}`"
                    class="hover:underline"
                >
                    {{ $t('more_search_results') }}
                </router-link>
            </li>
        </ul>
    </div>
</template>

<script setup lang="ts">
import { RecentSearchEntry } from '@src/types/GlobalSearch'
import { useSearch } from '@src/utils/stores/search'
import { storeToRefs } from 'pinia'

const props = defineProps<{
    title: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    entries: Record<string, any>[]
    count: number
    limit: number
    path: string
    entryPropertyInPath: string
    entryPropertyToShow: string
    listFilterName: string
    search: string | null
}>()

const searchStore = useSearch()

const { recentSearches: piniaRecentSearches } = storeToRefs(searchStore)

const handleRecentSearch = (entry: Record<string, unknown>) => {
    const rawLimit = import.meta.env.VITE_GLOBAL_SEARCH_ENTITY_RESULTS_LIMIT
    const parsedLimit = Number(rawLimit)
    const limit =
        Number.isFinite(parsedLimit) && parsedLimit > 0
            ? parsedLimit
            : props.limit

    const entryToAdd: RecentSearchEntry = {
        ...entry,
        entryPropertyInPath: props.entryPropertyInPath,
        entryPropertyToShow: props.entryPropertyToShow,
        listFilterName: props.listFilterName,
        title: props.title as RecentSearchEntry['title'],
        count: props.count,
        limit: props.limit,
        path: props.path,
        search: props.search ?? '',
    }

    let recentSearchesArray: RecentSearchEntry[] = []
    try {
        const stored = localStorage.getItem('recent-searches')
        if (stored) {
            const parsed = JSON.parse(stored)
            if (Array.isArray(parsed)) {
                recentSearchesArray = parsed as RecentSearchEntry[]
            }
        }
    } catch (error) {
        // If parsing fails, reset to empty array
        recentSearchesArray = []
    }

    const exists = recentSearchesArray.some(
        (item: RecentSearchEntry) =>
            JSON.stringify(item) === JSON.stringify(entryToAdd)
    )

    if (!exists) {
        if (recentSearchesArray.length >= limit) {
            recentSearchesArray.shift()
        }
        recentSearchesArray.push(entryToAdd)
    }

    localStorage.setItem('recent-searches', JSON.stringify(recentSearchesArray))
    piniaRecentSearches.value = recentSearchesArray as RecentSearchEntry[]
}
</script>

<style scoped></style>
